import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Button, 
  TextField, 
  Stack, 
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { addMonths, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../lib/supabase/AuthContext';
import { supabase } from '../lib/supabase/supabaseClient';
import UnsubscribeModal from '../components/UnsubscribeModal';
import EmailService from '../lib/resend/emailService';

const MonCompte: React.FC = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const [rdvMax, setRdvMax] = useState<number>(50);
  const [fullName, setFullName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setEmail(profile.email || '');
      setRdvMax(profile.max_rdv_per_month || 50);
    }
  }, [profile]);

  const handleRdvMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (value >= 8) {
      setRdvMax(value);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          max_rdv_per_month: rdvMax,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      setSuccessMessage('Profil mis à jour avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setErrorMessage(`Erreur: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleOpenUnsubscribeModal = () => {
    setUnsubscribeModalOpen(true);
  };

  const handleCloseUnsubscribeModal = () => {
    setUnsubscribeModalOpen(false);
  };

  const handleConfirmUnsubscribe = async () => {
    if (!user || !profile) return;

    try {
      // 1. Envoyer l'email de confirmation de désinscription
      console.log('Envoi de l\'email de confirmation de désinscription...');
      const emailResult = await EmailService.sendUnsubscribeConfirmation({
        email: profile.email,
        fullName: profile.full_name || undefined
      });

      if (!emailResult.success) {
        console.error('Erreur lors de l\'envoi de l\'email de confirmation:', emailResult.error);
        // On continue même si l'email échoue
      }

      // 2. Appeler la fonction Edge pour désactiver le compte (conserve les données)
      console.log('Désactivation du compte utilisateur...');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Non authentifié - veuillez vous connecter');
      }

      const { data, error } = await supabase.functions.invoke('deactivate-user-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Erreur lors de la désactivation du compte:', error);
        throw new Error(error.message || 'Erreur lors de la désactivation du compte');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Échec de la désactivation du compte');
      }

      console.log('Compte désactivé avec succès:', data);

      // 3. Nettoyage du localStorage/sessionStorage et redirection forcée
      console.log('Nettoyage des données locales et redirection...');
      
      // Nettoyer tous les stockages locaux
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirection forcée avec rechargement complet
      window.location.replace('/login?message=compte_desactive');

    } catch (error: any) {
      console.error('Erreur lors de la désinscription:', error);
      setErrorMessage(error.message || 'Une erreur est survenue lors de la désinscription. Veuillez réessayer.');
      throw error; // Remonter l'erreur à la modale
    }
  };

  // Calcule la prochaine date de renouvellement
  const getNextRenewalDate = () => {
    if (!user?.created_at) {
      return 'Date non disponible';
    }

    const createdAt = new Date(user.created_at);
    const today = new Date();
    
    // Calculer combien de mois se sont écoulés depuis la création
    let monthsElapsed = (today.getFullYear() - createdAt.getFullYear()) * 12 + 
                       (today.getMonth() - createdAt.getMonth());
    
    // Si nous sommes avant le jour de création du mois en cours, on est encore dans le mois précédent
    if (today.getDate() < createdAt.getDate()) {
      monthsElapsed--;
    }
    
    // La prochaine date de renouvellement est la date de création + (mois écoulés + 1) mois
    const nextRenewal = addMonths(createdAt, monthsElapsed + 1);
    
    return format(nextRenewal, 'dd/MM/yyyy', { locale: fr });
  };

  if (authLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Mon Compte
        </Typography>
        
        <Box sx={{ mt: 4 }}>
          <List>
            <ListItem>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Typography variant="h6" color="primary">
                  Informations personnelles
                </Typography>
                <Stack spacing={2} sx={{ width: '100%' }}>
                  <TextField
                    label="Nom complet"
                    fullWidth
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                  <TextField
                    label="Email"
                    fullWidth
                    value={email}
                    disabled
                    helperText="L'email ne peut pas être modifié"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSaveProfile}
                    disabled={saving}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Enregistrer'}
                  </Button>
                </Stack>
              </Stack>
            </ListItem>
            <Divider />
            <ListItem>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Typography variant="h6" color="primary">
                  Mon Abonnement
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>
                    Renouvellement le {getNextRenewalDate()}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="error"
                    size="small"
                    onClick={handleOpenUnsubscribeModal}
                  >
                    Se désabonner
                  </Button>
                </Box>
              </Stack>
            </ListItem>
            <Divider />
            <ListItem>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Typography variant="h6" color="primary">
                  Nombre de RDV maximum par mois
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    type="number"
                    value={rdvMax}
                    onChange={handleRdvMaxChange}
                    inputProps={{ min: 8 }}
                    helperText="Le nombre minimum de RDV est de 8 par mois"
                    size="small"
                    sx={{ flexGrow: 1, maxWidth: 200 }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSaveProfile}
                    disabled={saving}
                    size="small"
                  >
                    {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
                  </Button>
                </Box>
              </Stack>
            </ListItem>
            <Divider />
            <ListItem 
              button 
              onClick={() => window.location.href = 'mailto:melanie@growlyy.fr?subject=Demande de support - Dashboard Growlyy'}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(109, 76, 125, 0.05)',
                }
              }}
            >
              <ListItemText 
                primary="Support" 
                secondary="Besoin d'aide ? Contactez notre équipe"
              />
            </ListItem>
          </List>
        </Box>
      </Paper>
      
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      
      {/* Modale de désabonnement */}
      <UnsubscribeModal
        open={unsubscribeModalOpen}
        onClose={handleCloseUnsubscribeModal}
        onConfirm={handleConfirmUnsubscribe}
        userEmail={email}
      />
    </Container>
  );
};

export default MonCompte; 